import { FieldArray, Formik, useField } from 'formik';
import React, { useEffect, useState } from 'react';
import { deleteServiceDetail, freezeServiceDetails, getServiceDetails, IServiceDetail, updateServiceDetail } from '../../API/GoMoto';
import { useAppointmentState } from '../../context/Appointment/appointment';
import { Types, useCartDispatch } from '../../context/cart';
import { useKioskLocalization, useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { CTAButton } from '../../controls/ButtonBase';
import { DetailCard } from '../../controls/DetailCard/DetailCard';
import { DetailCardList } from '../../controls/DetailCard/DetailCardList';
import { Disclaimer } from '../../controls/Disclaimer/Disclaimer';
import { Heading } from '../../controls/Heading/Heading';
import { Spinner } from '../../controls/Spinner/Spinner';
import { ScrollableBody } from '../Layout/ScrollableBody';
import css from './Services.module.scss';
import { nameof } from '../../helpers/nameof';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMTitle } from '../../controls/Title';
import { GMForm, GMFormWrapper } from '../../controls/Form';

interface IProps {
    onProceed: () => void;
}

export const Services: React.FC<IProps> = React.memo((props) => {
    const Localization = useLocalization();
    const kioskLocalization = useKioskLocalization();
    const appointment = useAppointmentState();

    const {
        key: kioskKey,
        dealer: { key: dealerKey, serviceEditingEnabled },
    } = useKioskState();
    const [appointmentDetails, setAppointmentDetails] = useState<IServiceDetail[]>();
    const cartDispatch = useCartDispatch();

    const fetchServiceDetails = React.useCallback(() => {
        !!appointment.dmsAppointmentKey &&
            getServiceDetails(kioskKey, dealerKey, appointment.dmsAppointmentKey, appointment.key)
                .then((serviceDetails) => {
                    cartDispatch({ type: Types.ClearDetailItems });
                    setAppointmentDetails(serviceDetails);
                    serviceDetails.forEach((d) => cartDispatch({ type: Types.AddDetailItem,
                        payload: d }));
                });
    }, [appointment.dmsAppointmentKey, appointment.key, kioskKey, dealerKey, cartDispatch]);

    useEffect(() => {
        fetchServiceDetails();
    }, [fetchServiceDetails]);

    const onProceedEditable = async (serviceItems: IServiceDetail[]) => {
        if (appointmentDetails) {
            await Promise.all(
                appointmentDetails.map(async (detail) => {
                    const newDetail = serviceItems.find((s) => s.key === detail.key);

                    if (!newDetail) {
                        return await deleteServiceDetail(kioskKey, dealerKey, detail);
                    }
                    else if (newDetail.description !== detail.description) {
                        return await updateServiceDetail(kioskKey, dealerKey, newDetail);
                    }

                    return null;
                }),
            );
        }
        props.onProceed();
    };

    const onProceedNoneditable = async () => {
        if (appointmentDetails && !!appointment.dmsAppointmentKey) {
            await freezeServiceDetails(kioskKey, dealerKey, appointment.dmsAppointmentKey, appointment.key);
        }
        props.onProceed();
    };

    if (!appointmentDetails) {
        return <Spinner />;
    }
    else if (serviceEditingEnabled) {
        return <EditableDetailCardList serviceItems={appointmentDetails} onSubmit={onProceedEditable} />;
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={
                            <GMAppointmentName />

                        }
                        secondaryHeading={Localization.HereIsTheInformationWeHaveDownForYourVisitToday}
                        leftAlign
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            <CTAButton
                                styleType={'primary'}
                                type={'button'}
                                onClick={onProceedNoneditable}
                                title={Localization.ClickToProceedToNextStep}
                                label={Localization.Proceed}
                                id={Localization.ClickToProceedToNextStep}
                                ariaLabel={Localization.ClickToProceedToNextStep}
                            />,
                        ]}
                    />
                }
                topDivider
            >
                <div>
                    <GMTitle headerLevel={4}>{Localization.ServicesRequested}</GMTitle>
                    <DetailCardList>
                        {appointmentDetails.map((appDetail, index) => (
                            <ReadOnlyDetailCard
                                key={appDetail.key}
                                detail={appDetail}
                                index={index + 1}
                            />
                        ))}
                    </DetailCardList>
                </div>
                <div className={css.disclaimer}>
                    <p className={css.keys}>{kioskLocalization.dealer.echoPageReminderText}</p>
                </div>
                <Disclaimer />
            </ScrollableBody>
        );
    }
});

const ReadOnlyDetailCard = ({ detail, index }: { detail: IServiceDetail; index: number; }) => (
    <DetailCard
        name={detail.opCodeDescription}
        id={detail.opCodeDescription}
        title={detail.opCodeDescription}
        label={''}
        ariaLabel={detail.opCodeDescription}
        index={index}
        details={detail.description}
    />
);

const EditableDetailCardList = (props: { serviceItems: IServiceDetail[]; onSubmit: (serviceItems: IServiceDetail[]) => Promise<void>; }) => {
    const kioskLocalization = useKioskLocalization();
    const initialValues = { serviceItems: props.serviceItems };
    const Localization = useLocalization();

    return (
        <Formik initialValues={initialValues} onSubmit={({ serviceItems }) => props.onSubmit(serviceItems)}>
            {({ values, submitForm }) => (
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={
                                <GMAppointmentName />
                            }
                            secondaryHeading={Localization.HereIsTheInformationWeHaveDownForYourVisitToday}
                            leftAlign
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            onClick={submitForm}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                            id={Localization.ClickToProceedToNextStep}
                        />
                    }
                >
                    <FieldArray name={nameof<typeof values>('serviceItems')}>
                        {({ remove }) =>
                            values.serviceItems.map((appDetail, index) => (
                                <EditableDetailCard
                                    id={appDetail.key}
                                    name={`${nameof<typeof values>('serviceItems')}.${index}`}
                                    key={appDetail.key}
                                    serviceItem={appDetail}
                                    index={index + 1}
                                    onDelete={() => remove(index)}
                                />
                            ))
                        }
                    </FieldArray>
                    <div className={css.disclaimer}>
                        <p className={css.keys}>{kioskLocalization.dealer.echoPageReminderText}</p>
                    </div>
                    <Disclaimer />
                </GMForm>
            )}
        </Formik>
    );
};

interface IServiceItemLineComment<T extends IServiceDetail> {
    index: number;
    name: string;
    id: string;
    /** The service item to display */
    serviceItem: T;
    onDelete: () => void;
}

const EditableDetailCard = <T extends IServiceDetail>(props: IServiceItemLineComment<T>) => {
    const Localization = useLocalization();

    const [
        {
            value: { description, ...rest },
            onBlur,
        },
        { error },
        { setValue },
    ] = useField<T>({ name: props.name });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setValue({ ...rest,
            description: e.target.value } as T);
    };

    return (
        <GMFormWrapper fullWidth>
            <DetailCard
                name={props.name}
                id={props.id}
                title={props.serviceItem.opCodeDescription}
                label={props.serviceItem.opCodeDescription}
                ariaLabel={props.serviceItem.opCodeDescription}
                index={props.index}
                details={description || ''}
                editable
                placeholder={Localization.NoComment}
                onChangeCallback={handleChange}
                onBlurCallback={() => onBlur}
                error={error}
                onDelete={props.onDelete}
            />
        </GMFormWrapper>
    );
};
